// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./ServiceHistory.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Button,
	CircularProgress,
	IconButton,
	Pagination,
} from "@mui/material";

// MUI ICONS
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import ServiceHistoryEditAddDialog from "./ServiceHistoryEditAddDialog/ServiceHistoryEditAddDialog";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	checkboxSort,
	dateSort,
	defaultSort,
	notApplicable,
	strictSort,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const ServiceHistory = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [serviceHistories, setServiceHistories] = useState([]);

	const [serviceHistoriesRows, setServiceHistoriesRows] = useState([]);
	const [serviceHistoriesColumns, setServiceHistoriesColumns] = useState([]);

	const [incidentTypes, setIncidentTypes] = useState([]);
	const [devices, setDevices] = useState([]);
	// eventually more device types here

	const [recordToEdit, setRecordToEdit] = useState({});
	const [loading, setLoading] = useState(false);
	const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
	const [editing, setEditing] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Service History",
			employeeID: employeeID,
			notes: "Accessed the Service History page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		handleAPICalls();
	}, []);

	useEffect(() => {
		if (serviceHistories.length > 0 && incidentTypes.length > 0) {
			createColumnData();
			createRowData();
		}
	}, [serviceHistories, incidentTypes]);

	// INPUT HANDLERS
	const handleAddClick = () => {
		setEditing(false);
		setEditAddDialogOpen(true);
	};

	const handleEditClick = (record) => {
		setRecordToEdit(record);
		setEditing(true);
		setEditAddDialogOpen(true);
	};

	const handleEditAddDialogClose = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
		handleAPICalls();
	};

	// API FUNCTIONS

	const handleAPICalls = async () => {
		setLoading(true);
		await fetchServiceHistories();
		await fetchIncidentTypes();
		await fetchDevices();
		setLoading(false);
	};

	const fetchServiceHistories = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hardwareInventory/serviceHistories/all"
			);
			setServiceHistories(res.data);
			return res.data;
		} catch (error) {
			console.error(error);
		}
	};

	const fetchIncidentTypes = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hardwareInventory/incidentTypes/all"
			);
			setIncidentTypes(res.data);
			return res.data;
		} catch (error) {
			console.error(error);
		}
	};

	const fetchDevices = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hardwareInventory/all"
			);
			setDevices(res.data);
			return res.data;
		} catch (error) {
			console.error(error);
		}
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setServiceHistoriesColumns([
			{
				accessorKey: "created",
				header: () => <span>Created</span>,
				label: "Created",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "ticket_number",
				header: () => <span>Ticket Number</span>,
				label: "Ticket Number",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: strictSort,
			},
			{
				accessorKey: "incident_type_display_name",
				header: () => <span>Incident Type</span>,
				label: "Incident Type",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: incidentTypes.map((type) => ({
					id: type.id,
					label: type.display_name,
				})),
			},
			{
				accessorKey: "j2_device_name",
				header: () => <span>Device Name</span>,
				label: "Device Name",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "category_display_name",
				header: () => <span>Device Category</span>,
				label: "Device Category",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "checkbox",
				filterFn: checkboxSort,
				filterOptions: [
					{ id: "Laptops & Desktops", label: "Laptops & Desktops" },
				],
			},
			{
				accessorKey: "notes",
				header: () => <span>Notes</span>,
				label: "Notes",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: (props) => (
					<IconButton
						onClick={() =>
							handleEditClick(props.row.original.serviceHistory)
						}
						disableRipple={true}
						sx={{
							justifyContent: "flex-end",
						}}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const createRowData = () => {
		setServiceHistoriesRows(
			serviceHistories.map((record) => ({
				serviceHistory: record, // This is used to pass the entire record for editing
				created: convertToPST(record.created),
				ticket_number: record.ticket_number,
				incident_type_display_name: record.incident_type_display_name,
				j2_device_name: record.j2_device_name,
				category_display_name: record.category_display_name,
				notes: record.notes,
			}))
		);
	};

	// RENDER
	return (
		<div className="ServiceHistories">
			<div className="ServiceHistoriesHeader">
				<div className="ServiceHistoriesHeaderTitle">
					Service History
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddClick}
						disabled={loading}
					>
						New Service History Record
					</Button>
				</div>
			</div>

			{loading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!loading && (
				<div className="tableContainerDiv">
					{serviceHistories.length === 0 && <J2NoResultsFound />}
					{serviceHistories.length > 0 &&
						serviceHistoriesRows.length > 0 &&
						serviceHistoriesColumns.length > 0 && (
							<TanStackTable
								columnData={serviceHistoriesColumns}
								rowData={serviceHistoriesRows}
							/>
						)}
				</div>
			)}

			<ServiceHistoryEditAddDialog
				open={editAddDialogOpen}
				onClose={handleEditAddDialogClose}
				recordToEdit={recordToEdit}
				devices={devices}
				fullServiceHistories={serviceHistories}
				editing={editing}
				incidentTypes={incidentTypes}
			/>
		</div>
	);
};

// EXPORT
export default ServiceHistory;
